/**
 * Sign in.
 *
 * Three behaviours here are dictated by the backend and are not stylistic:
 *
 * 1. A FAILED LOGIN SAYS NOTHING SPECIFIC. `authenticate_staff` returns the same 401
 *    for a wrong password, an unknown address, a suspended account and a valid login
 *    with no staff membership anywhere. This screen shows that message as given and
 *    does not embellish it. "No account with that email" would turn the form into an
 *    account-enumeration oracle, and for a legal product the mere fact that an address
 *    belongs to a firm is disclosive.
 *
 * 2. THE ORGANIZATION CHOICE IS A SUCCESS, NOT A FAILURE. A login holding several
 *    active memberships gets a 200 carrying the options. The credentials were right;
 *    the system needs to be told which firm and will not guess. We re-submit with
 *    `organization_id` — the same credentials, one more field.
 *
 * 3. THE TENANT COMES FROM THE MEMBERSHIP, NEVER FROM THIS FORM. `organization_id`
 *    selects among memberships the caller already holds and is validated against them
 *    server-side; it cannot introduce a tenant (BR-02).
 */

import { isUnauthenticated, userMessage, type MembershipOption } from '@legalcrm/api';
import {
  Button,
  Card,
  CardBody,
  Form,
  FormActions,
  FormInput,
  Spinner,
  useZodForm,
} from '@legalcrm/ui';
import { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { useAuth } from '@/auth/AuthProvider';

const schema = z.object({
  email: z.string().min(1, 'Enter your email address').email('Enter a valid email address'),
  password: z.string().min(1, 'Enter your password'),
});

type LoginValues = z.infer<typeof schema>;

export function LoginPage() {
  const { signIn, isAuthenticated, organizationChoices, clearOrganizationChoices } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<unknown>(null);
  const [submitting, setSubmitting] = useState(false);

  const form = useZodForm(schema, { defaultValues: { email: '', password: '' } });

  /**
   * A 401 means different things on different routes, and the default wording is
   * wrong here.
   *
   * Everywhere else in the app a 401 means the session died, and `userMessage` says
   * "please sign in again" — which on the sign-in screen is both circular and
   * alarming. On THIS route a 401 is the backend's uniform refusal: wrong password,
   * unknown address, suspended account and "no staff membership anywhere" all
   * produce it, deliberately indistinguishably.
   *
   * So we substitute the credential wording — and, just as deliberately, we do not
   * try to be more specific than the server was. "No account with that email" would
   * turn this form into an account-enumeration oracle, and for a legal product the
   * mere fact that an address belongs to a firm is disclosive.
   */
  const loginError =
    error && isUnauthenticated(error)
      ? 'Email address or password is incorrect.'
      : error
        ? userMessage(error)
        : null;

  const destination = (location.state as { from?: { pathname: string } } | null)?.from?.pathname;

  if (isAuthenticated) return <Navigate to={destination ?? '/'} replace />;

  const attempt = async (values: LoginValues, organizationId?: string) => {
    setError(null);
    setSubmitting(true);
    try {
      const outcome = await signIn({
        email: values.email,
        password: values.password,
        ...(organizationId ? { organization_id: organizationId } : {}),
      });
      if (outcome === 'signed-in') navigate(destination ?? '/', { replace: true });
    } catch (caught) {
      setError(caught);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-6 text-center">
          <div
            className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-lg font-bold text-primary-ink"
            aria-hidden="true"
          >
            L
          </div>
          <h1 className="text-xl font-semibold text-ink">Sign in</h1>
          <p className="mt-1 text-sm text-ink-muted">Legal CRM — staff</p>
        </div>

        <Card>
          <CardBody>
            {organizationChoices ? (
              <OrganizationChoice
                options={organizationChoices}
                busy={submitting}
                onCancel={() => {
                  clearOrganizationChoices();
                  form.reset();
                }}
                onChoose={(organizationId) =>
                  attempt(form.getValues(), organizationId)
                }
              />
            ) : (
              <Form
                form={form}
                // Field-level errors (a 400) still route to their inputs; the
                // credential refusal is rendered above with its own wording.
                error={error && isUnauthenticated(error) ? undefined : error}
                ariaLabel="Sign in"
                onSubmit={(values) => attempt(values)}
              >
                {loginError && isUnauthenticated(error) && (
                  <div
                    role="alert"
                    className="rounded border border-danger/30 bg-danger-subtle px-3 py-2 text-sm text-ink"
                  >
                    {loginError}
                  </div>
                )}
                <FormInput
                  form={form}
                  name="email"
                  label="Email address"
                  type="email"
                  autoComplete="username"
                  autoFocus
                  required
                />
                <FormInput
                  form={form}
                  name="password"
                  label="Password"
                  type="password"
                  autoComplete="current-password"
                  required
                />
                <FormActions submitLabel="Sign in" busy={submitting} />
              </Form>
            )}
          </CardBody>
        </Card>

        <p className="mt-4 text-center text-xs text-ink-subtle">
          Trouble signing in? Contact your firm&rsquo;s administrator.
        </p>
      </div>
    </div>
  );
}

/**
 * Which firm. Only reachable when the server said a choice is required, and only
 * listing memberships the server itself returned — this is a selection among things
 * the caller already holds, not a way to reach a firm they do not.
 */
function OrganizationChoice({
  options,
  busy,
  onChoose,
  onCancel,
}: {
  options: MembershipOption[];
  busy: boolean;
  onChoose: (organizationId: string) => void;
  onCancel: () => void;
}) {
  return (
    <div className="flex flex-col gap-3">
      <div>
        <h2 className="text-base font-semibold text-ink">Choose a firm</h2>
        <p className="mt-0.5 text-sm text-ink-muted">
          You hold a membership at more than one. Your session is bound to whichever you
          choose.
        </p>
      </div>

      <ul className="flex flex-col gap-2">
        {options.map((option) => (
          <li key={option.organization_id}>
            <button
              type="button"
              disabled={busy}
              onClick={() => onChoose(option.organization_id)}
              className="flex w-full items-center justify-between gap-3 rounded border border-line-strong px-3 py-2.5 text-left transition-colors hover:bg-surface-sunken disabled:opacity-50"
            >
              <span className="min-w-0">
                <span className="block truncate text-base font-medium text-ink">
                  {option.organization_name}
                </span>
                <span className="block text-xs text-ink-muted">{option.role_key}</span>
              </span>
              {busy ? (
                <Spinner size="xs" label={null} />
              ) : (
                <span aria-hidden="true" className="text-ink-subtle">
                  &rarr;
                </span>
              )}
            </button>
          </li>
        ))}
      </ul>

      <Button variant="ghost" size="sm" onClick={onCancel} disabled={busy}>
        Use a different account
      </Button>
    </div>
  );
}
